// Service centralisé pour gérer les messages d'erreur
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface ErrorMessage {
  summary: string;
  detail: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorMessageService {

  /**
   * Convertit une HttpErrorResponse en message d'erreur compréhensible
   */
  getErrorMessage(error: HttpErrorResponse): ErrorMessage {
    // Erreur réseau (pas de connexion)
    if (error.status === 0) {
      return {
        summary: 'Connexion impossible',
        detail: 'Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet et réessayer.'
      };
    }

    // Erreur client (navigateur)
    if (error.error instanceof ErrorEvent) {
      return {
        summary: 'Erreur locale',
        detail: error.error.message || 'Une erreur s\'est produite dans votre navigateur. Veuillez rafraîchir la page.'
      };
    }

    // Erreurs serveur - extraction du message
    const serverError = error.error;
    let detail = this.extractDetailFromServerError(serverError, error);
    
    // Déterminer le résumé en fonction du contenu, pas du code HTTP
    const summary = this.determineSummary(error, detail);

    return { summary, detail };
  }

  /**
   * Extrait le message détaillé de l'erreur serveur
   */
  private extractDetailFromServerError(serverError: any, error: HttpErrorResponse): string {
    // Détecter si c'est une page HTML (erreur serveur 500)
    if (typeof serverError === 'string') {
      // Vérifier si c'est du HTML
      if (serverError.includes('<!DOCTYPE') || serverError.includes('<html')) {
        return 'Le serveur rencontre un problème temporaire. Veuillez réessayer dans quelques instants.';
      }
      return serverError;
    }
    
    // Si c'est un objet avec des détails
    if (typeof serverError === 'object' && serverError !== null) {
      // Backend ALPTIS gateway format:
      // { message, status, upstream_status, body }
      // where `body` can be a JSON string containing { causes: [...] }.
      if (typeof serverError.body === 'string' && serverError.body.trim()) {
        const upstreamParsed = this.tryParseJson(serverError.body);
        if (upstreamParsed && Array.isArray(upstreamParsed.causes) && upstreamParsed.causes.length > 0) {
          const causeMessage = upstreamParsed.causes
            .map((c: any) => c?.message || c?.champ)
            .filter((m: any) => typeof m === 'string' && m.trim())
            .join(' | ');
          if (causeMessage) {
            return causeMessage;
          }
        }

        if (upstreamParsed?.message && typeof upstreamParsed.message === 'string') {
          return upstreamParsed.message;
        }

        return serverError.body;
      }

      // Essayer detail (FastAPI)
      if (serverError.detail) {
        return this.formatDetail(serverError.detail);
      }
      
      // Essayer message
      if (serverError.message) {
        return serverError.message;
      }
      
      // Essayer error
      if (serverError.error) {
        return serverError.error;
      }

      // Si c'est un tableau d'erreurs de validation (FastAPI 422)
      if (Array.isArray(serverError) && serverError.length > 0) {
        return this.formatValidationErrors(serverError);
      }
    }

    // Message par défaut basé sur le contexte
    return this.getDefaultMessageForStatus(error.status);
  }

  /**
   * Formate le détail de l'erreur (peut être string ou objet)
   */
  private formatDetail(detail: any): string {
    if (typeof detail === 'string') {
      return detail;
    }
    
    if (typeof detail === 'object') {
      // Si c'est un objet avec un message
      if (detail.message) {
        return detail.message;
      }
      // Sinon, convertir en JSON lisible
      return JSON.stringify(detail, null, 2);
    }
    
    return String(detail);
  }

  /**
   * Formate les erreurs de validation FastAPI (422)
   */
  private formatValidationErrors(errors: any[]): string {
    const messages = errors.map(err => {
      const field = err.loc ? err.loc.join('.') : 'champ inconnu';
      const msg = err.msg || 'erreur de validation';
      return `${field}: ${msg}`;
    });
    
    if (messages.length === 1) {
      return messages[0];
    }
    
    return 'Erreurs de validation:\n' + messages.join('\n');
  }

  /**
   * Détermine le résumé basé sur le contexte de l'erreur
   */
  private determineSummary(error: HttpErrorResponse, detail: string): string {
    const url = error.url || '';
    const lowerDetail = detail.toLowerCase();

    // Réponse HTML au lieu de JSON (backend indisponible ou erreur de proxy)
    // IMPORTANT: Cette vérification doit être AVANT l'authentification car
    // "Unexpected token" (erreur JSON parse) contient le mot "token" et serait
    // faussement classée comme erreur d'authentification.
    if (lowerDetail.includes('unexpected token') ||
        lowerDetail.includes('<!doctype') ||
        lowerDetail.includes('json attendu') ||
        lowerDetail.includes('page d\'erreur html')) {
      return 'Serveur temporairement indisponible';
    }

    // Erreur serveur temporaire (HTML au lieu de JSON)
    if (lowerDetail.includes('serveur rencontre un problème') || 
        lowerDetail.includes('temporairement indisponible')) {
      return 'Serveur temporairement indisponible';
    }

    // Authentification — on utilise 'token d' ou 'token invalid' pour éviter
    // les faux positifs avec "Unexpected token" (erreur JSON parse)
    if (lowerDetail.includes('authentification') || 
        lowerDetail.includes('token d\'') ||
        lowerDetail.includes('token invalid') ||
        lowerDetail.includes('token expiré') ||
        lowerDetail.includes('access_token') ||
        lowerDetail.includes('connexion') ||
        lowerDetail.includes('expired') ||
        lowerDetail.includes('invalid credentials')) {
      return 'Problème d\'authentification';
    }

    // Validation de données
    if (lowerDetail.includes('validation') || 
        lowerDetail.includes('invalide') || 
        lowerDetail.includes('requis') ||
        lowerDetail.includes('incorrect')) {
      return 'Données invalides';
    }

    // Permission
    if (lowerDetail.includes('permission') || 
        lowerDetail.includes('autorisé') || 
        lowerDetail.includes('accès refusé')) {
      return 'Accès refusé';
    }

    // Doublon
    if (lowerDetail.includes('existe déjà') || 
        lowerDetail.includes('doublon') || 
        lowerDetail.includes('duplicate')) {
      return 'Élément existant';
    }

    // Non trouvé
    if (lowerDetail.includes('introuvable') || 
        lowerDetail.includes('not found') || 
        lowerDetail.includes('n\'existe pas')) {
      return 'Élément introuvable';
    }

    // Erreurs API spécifiques
    if (url.includes('april') || lowerDetail.includes('april')) {
      return 'Erreur APRIL';
    }
    if (url.includes('cardif') || lowerDetail.includes('cardif')) {
      return 'Erreur Cardif';
    }
    if (url.includes('neoliane') || lowerDetail.includes('neoliane')) {
      return 'Erreur Neoliane';
    }
    if (url.includes('swisslife') || lowerDetail.includes('swisslife')) {
      return 'Erreur SwissLife';
    }
    if (url.includes('hubspot') || lowerDetail.includes('hubspot')) {
      return 'Erreur HubSpot';
    }
    if (url.includes('/alptis/') || lowerDetail.includes('alptis')) {
      return 'Erreur ALPTIS';
    }

    // Comparaison
    if (url.includes('comparison') || url.includes('comparaison')) {
      return 'Erreur de comparaison';
    }

    // Devis
    if (url.includes('devis') || url.includes('quote')) {
      return 'Erreur de devis';
    }

    // Par défaut - message générique sans code HTTP
    return 'Une erreur est survenue';
  }

  /**
   * Messages par défaut si aucun détail n'est disponible
   */
  private getDefaultMessageForStatus(status: number): string {
    // Même si on a un code, on retourne un message compréhensible
    if (status >= 500) {
      return 'Le serveur rencontre un problème. Veuillez réessayer dans quelques instants.';
    }
    
    if (status === 404) {
      return 'La ressource demandée est introuvable.';
    }
    
    if (status === 403) {
      return 'Vous n\'avez pas les permissions nécessaires pour cette action.';
    }
    
    if (status === 401) {
      return 'Votre session a expiré. Veuillez vous reconnecter.';
    }
    
    if (status === 400) {
      return 'Les données envoyées sont invalides. Veuillez vérifier vos informations.';
    }

    if (status === 422) {
      return 'Certains champs sont invalides. Veuillez vérifier vos données.';
    }
    
    return 'Une erreur inattendue s\'est produite. Veuillez réessayer.';
  }

  private tryParseJson(raw: string): any | null {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Formate spécialement les erreurs APRIL (garanties)
   */
  formatAprilGuaranteeError(message: string): ErrorMessage {
    if (message.includes('guaranteeCode')) {
      return {
        summary: 'Format de garantie incorrect',
        detail: 'Les codes de garantie doivent utiliser les noms complets: DECES au lieu de DC, INVALIDITE_PERMANENTE_TOTALE au lieu d\'IPT, INCAPACITE_TEMPORAIRE_TOTALE au lieu d\'ITT.'
      };
    }
    
    return {
      summary: 'Erreur APRIL',
      detail: message
    };
  }
}
